import { LiliPlanType } from '@Pages/happyFlowPages/planSelection/types.ts';

export interface ICustomerSlice {
  customer: CustomerData;
  setCustomerData: (data: CustomerData) => void;
  getCustomerData: () => CustomerData;
  isMultiBusiness: () => boolean;
}

export interface CustomerData {
  externalId?: string;
  customerId?: string;
  partnerId?: string;
  accessToken?: string;
  customerLogoUrl?: string;
  onboardingStatus?: OnboardingStatus;
  nameTooLong?: boolean;
  onboardingStep?: OnboardingStep;
  plan?: LiliPlanType;
  planDiscountContent?: string;
  business?: { status: BusinessStatus };

  isPersonalAndBusinessAddressSame?: boolean;
  customerData: {
    email?: string;
    password?: string;
    phoneNumber?: string;
    firstName?: string;
    lastName?: string;
    birthDate?: string;
    ssn?: string;
    customerBusinessData: CustomerBusinessData;
    onboardingTypeEnum: OnboardingTypeEnum;

    customerAddressData: AddressData;
    businessAddressData: AddressData;
    customerEdd?: string;
    idNumber?: string;
    idType?: string;
    idCountry?: string;
    idGovNumber?: string;
    idExpirationDate?: string;
    partner?: boolean;
    pro?: boolean;
    lastStepData?: IPickUpWhreYouLeft;
  };
  allowEditingPersonalDetails?: boolean;
  hasSoleProprietor?: boolean;
  availablePromoCodes?: IAvailablePromoCodes;
  bank?: bankTypes;

  isNonUsSupported?: boolean;
  idCountry?: string;
  nationalIdDescription?: string;
}

export interface IPickUpWhreYouLeft {
  version: number;
  stepName: string;
}
interface CustomerBusinessData {
  externalId?: string;
  segment?: string;
  naicsIndustry?: string;
  mainIncomeSource?: MAIN_INCOME_SOURCE;
  businessName?: string;
  incorporationState?: string;
  ein?: string;
  ownershipPercent?: number;
  businessUbos?: {
    uboResponseList?: UboResponse[];
  };
}

export interface UboResponse {
  externalId: string;
  email: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  ssn: string;
  birthDate: string;
  customerAddressData: AddressData;
  role: UBORoles;
  ownershipPercent: number;
  allowEditing: boolean;
}

export enum UBORoles {
  PARTNER = 'PARTNER',
  PARTNER_FULL_ACCESS = 'PARTNER_FULL_ACCESS',
}

export interface AddressData {
  city: string;
  state: string;
  line1: string;
  line2: string;
  postalCode: string;
  country?: string;
  sameAsPrimary: boolean;
}

export interface IAvailablePromoCodes {
  PREMIUM?: string;
  PRO_V2?: string;
  SMART?: string;
}

export type bankTypes = 'SUNRISE' | 'CHOICE';
export enum MAIN_INCOME_SOURCE {
  S_CORP = 'S_CORP',
  C_CORP = 'C_CORP',
  GENERAL_PARTNERSHIP = 'GENERAL_PARTNERSHIP',
  LIMITED_LIABILITY_PARTNERSHIP = 'LLP',
  MULTI_MEMBER_LLC = 'MMLLC',
  SINGLE_MEMBER_LLC = 'LLC',
  SOLE_PROP_DBA = 'DBA',
  SOLE_PROP_NO_DBA = 'SSN',
}

export enum OnboardingStep {
  SET_NAME = 'SET_NAME',
  SET_PHONE = 'SET_PHONE',
  SET_IDENTITY = 'SET_IDENTITY',
  SET_ADDRESS = 'SET_ADDRESS',
  SET_BUSINESS_ENTITY = 'SET_BUSINESS_ENTITY',
  SET_BUSINESS_ADDRESS_DETAILS = 'SET_BUSINESS_ADDRESS_DETAILS',
  SET_EDD = 'SET_EDD',
  CREATE_UBOS = 'CREATE_UBOS',
  SET_PLAN = 'SET_PLAN',
  SUBMITTED = 'SUBMITTED',
}
export enum OnboardingTypeEnum {
  STANDARD = 'STANDARD',
  MULTI_BUSINESS = 'MULTI_BUSINESS',
  PASSWORD_LESS = 'PASSWORD_LESS',
  NO_CONTINUE_BLOCK = 'NO_CONTINUE_BLOCK',
  NO_CONTINUE_PRIMARY = 'NO_CONTINUE_PRIMARY',
}

export enum BusinessStatus {
  NEW = 'NEW',
  PENDING = 'PENDING',
  RESUBMIT = 'RESUBMIT',
  APPROVED = 'APPROVED',
  PENDING_MIDDESK = 'PENDING_MIDDESK',
  NONE = 'NONE',
  KYC_PENDING = 'KYC_PENDING',
}

export enum OnboardingStatus {
  DURING_REGISTRATION = 'DURING_REGISTRATION',
  KYC_PASSED = 'KYC_PASSED',
  COMPLETED = 'COMPLETED',
  KYC_FIRST_FAILED = 'KYC_FIRST_FAILED',
  KYC_FAILED = 'KYC_FAILED',
  KYC_FAILED_PENDING = 'KYC_FAILED_PENDING',
  KYC_FAILED_UPLOADED_DOCS = 'KYC_FAILED_UPLOADED_DOCS',
  KYB_PENDING = 'KYB_PENDING',
  KYB_FAILED_FINAL = 'KYB_FAILED_FINAL',
  KYC_FAILED_PARTNER = 'KYC_FAILED_PARTNER',
  KYC_FAILED_FINAL = 'KYC_FAILED_FINAL',
}
import { z } from 'zod';

const AddressDataSchema = z
  .object({
    city: z.string().optional(),
    state: z.string().optional(),
    line1: z.string().optional(),
    line2: z.string().optional(),
    postalCode: z.string().optional(),
    country: z.string().optional(),
    sameAsPrimary: z.boolean().optional(),
  })
  .optional();

const UboResponseSchema = z.object({
  externalId: z.string().optional(),
  email: z.string().optional(),
  phoneNumber: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  ssn: z.string().optional(),
  birthDate: z.string().optional(),
  customerAddressData: AddressDataSchema,
  role: z.enum(['PARTNER', 'PARTNER_FULL_ACCESS']).optional(),
  ownershipPercent: z.number().optional(),
  allowEditing: z.boolean().optional(),
});

const CustomerBusinessDataSchema = z.object({
  externalId: z.string().optional(),
  segment: z.string().optional(),
  naicsIndustry: z.string().optional(),
  mainIncomeSource: z.nativeEnum(MAIN_INCOME_SOURCE).optional(),
  businessName: z.string().optional(),
  incorporationState: z.string().optional(),
  ein: z.string().optional(),
  ownershipPercent: z.number().optional(),
  businessUbos: z
    .object({
      uboResponseList: z.array(UboResponseSchema).optional(),
    })
    .optional()
    .optional(),
});

export const CustomerDataSchema = z.object({
  externalId: z.string().optional(),
  customerId: z.string().optional(),
  partnerId: z.string().optional(),
  accessToken: z.string().optional(),
  customerLogoUrl: z.string().optional(),
  onboardingStatus: z.nativeEnum(OnboardingStatus).optional(),
  nameTooLong: z.boolean().optional(),
  onboardingStep: z.nativeEnum(OnboardingStep).optional(),
  plan: z.any().optional(), // Replace with LiliPlanType schema if available
  planDiscountContent: z.string().optional(),
  business: z
    .object({
      status: z.nativeEnum(BusinessStatus),
    })
    .optional(),
  isPersonalAndBusinessAddressSame: z.boolean().optional(),
  customerData: z.object({
    email: z.string().optional(),
    password: z.string().optional(),
    phoneNumber: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    birthDate: z.string().optional(),
    ssn: z.string().optional(),
    customerBusinessData: CustomerBusinessDataSchema,
    onboardingTypeEnum: z.nativeEnum(OnboardingTypeEnum).optional(),
    customerAddressData: AddressDataSchema,
    businessAddressData: AddressDataSchema,
    customerEdd: z.string().optional(),
    idNumber: z.string().optional(),
    idType: z.string().optional(),
    idCountry: z.string().optional(),
    idGovNumber: z.string().optional(),
    idExpirationDate: z.string().optional(),
    partner: z.boolean().optional(),
    pro: z.boolean().optional(),
    lastStepData: z
      .object({
        version: z.number(),
        stepName: z.string(),
      })
      .optional(),
  }),
  allowEditingPersonalDetails: z.boolean().optional(),
  hasSoleProprietor: z.boolean().optional(),
  availablePromoCodes: z
    .object({
      PREMIUM: z.string().optional(),
      PRO_V2: z.string().optional(),
      SMART: z.string().optional(),
    })
    .optional(),
  bank: z.enum(['SUNRISE', 'CHOICE']).optional(),
  isNonUsSupported: z.boolean().optional(),
  idCountry: z.string().optional(),
  nationalIdDescription: z.string().optional(),
});
